/**
 * Sea Monster Renderer
 * 
 * Renders large shadow shapes moving beneath the ocean surface.
 * These are purely visual/atmospheric with occasional loot rewards.
 * Uses grid-aligned square shapes to match the game's aesthetic.
 */

import Phaser from 'phaser';
import { TILE_SIZE } from '../types';

// Monster type configurations for visual rendering
const MONSTER_VISUALS: Record<string, {
  color: number;
  segments: number;
  segmentSpacing: number;
  pulseSpeed: number;
}> = {
  leviathan: {
    color: 0x1a0a2e,
    segments: 8,
    segmentSpacing: 20,
    pulseSpeed: 0.002,
  },
  kraken: {
    color: 0x0a1a1a,
    segments: 6,
    segmentSpacing: 16,
    pulseSpeed: 0.003,
  },
  sea_serpent: {
    color: 0x0a2a1a,
    segments: 10,
    segmentSpacing: 12,
    pulseSpeed: 0.004,
  },
  giant_whale: {
    color: 0x1a1a2a,
    segments: 5,
    segmentSpacing: 24,
    pulseSpeed: 0.0015,
  },
  abyssal_shadow: {
    color: 0x0a0a0a,
    segments: 12,
    segmentSpacing: 18,
    pulseSpeed: 0.001,
  },
};

interface SeaMonsterSprite {
  id: string;
  type: string;
  container: Phaser.GameObjects.Container;
  graphics: Phaser.GameObjects.Graphics;
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  speed: number;
  size: number;
  phase: number;
}

export class SeaMonsterRenderer {
  private scene: Phaser.Scene;
  private monsters: Map<string, SeaMonsterSprite> = new Map();
  private updateTimer: Phaser.Time.TimerEvent | null = null;
  private globalTime: number = 0;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Start the rendering system
   */
  start(): void {
    this.updateTimer = this.scene.time.addEvent({
      delay: 100,  // Update every 100ms
      callback: () => this.updateAnimations(),
      loop: true,
    });
  }

  /**
   * Add a new sea monster
   */
  addMonster(data: {
    id: string;
    type: string;
    x: number;
    y: number;
    targetX: number;
    targetY: number;
    speed: number;
    size: number;
    description: string;
  }): void {
    if (this.monsters.has(data.id)) return;

    const container = this.scene.add.container(data.x, data.y);
    container.setDepth(-8);  // Below ships, above water

    const graphics = this.scene.add.graphics();
    container.add(graphics);

    const sprite: SeaMonsterSprite = {
      id: data.id,
      type: data.type,
      container,
      graphics,
      x: data.x,
      y: data.y,
      targetX: data.targetX,
      targetY: data.targetY,
      speed: data.speed,
      size: data.size,
      phase: Math.random() * Math.PI * 2,
    };

    this.monsters.set(data.id, sprite);
    this.drawMonster(sprite);

    console.log(`🐙 Sea monster rendered: ${data.type} at (${Math.round(data.x)}, ${Math.round(data.y)})`);
  }

  /**
   * Update monster positions from server
   */
  updatePositions(monsters: Array<{
    id: string;
    type: string;
    x: number;
    y: number;
    size: number;
  }>): void {
    for (const data of monsters) {
      const sprite = this.monsters.get(data.id);
      if (sprite) {
        sprite.x = data.x;
        sprite.y = data.y;
        sprite.container.setPosition(data.x, data.y);
      }
    }
  }

  /**
   * Remove a sea monster
   */
  removeMonster(id: string): void {
    const sprite = this.monsters.get(id);
    if (sprite) {
      // Fade out animation
      this.scene.tweens.add({
        targets: sprite.container,
        alpha: 0,
        duration: 2000,
        onComplete: () => {
          sprite.container.destroy();
          this.monsters.delete(id);
        },
      });
    }
  }

  /**
   * Draw a sea monster shadow
   */
  private drawMonster(sprite: SeaMonsterSprite): void {
    const { graphics, type, size, phase } = sprite;
    const visuals = MONSTER_VISUALS[type] || MONSTER_VISUALS.sea_serpent;

    graphics.clear();

    // Calculate direction for segment orientation
    const dx = sprite.targetX - sprite.x;
    const dy = sprite.targetY - sprite.y;
    const angle = Math.atan2(dy, dx);

    // Draw segmented body (grid-aligned squares)
    const baseSize = TILE_SIZE * size;
    const segmentSize = baseSize / visuals.segments;

    for (let i = 0; i < visuals.segments; i++) {
      // Offset each segment along the direction of travel
      const segmentOffset = i * visuals.segmentSpacing;
      const offsetX = -Math.cos(angle) * segmentOffset;
      const offsetY = -Math.sin(angle) * segmentOffset;

      // Pulsing size variation
      const pulse = Math.sin(this.globalTime * visuals.pulseSpeed + phase + i * 0.5);
      const sizeVariation = 1 + pulse * 0.15;

      // Segments get smaller toward the tail
      const tailFactor = 1 - (i / visuals.segments) * 0.5;
      const finalSize = segmentSize * sizeVariation * tailFactor;

      // Alpha fades toward tail
      const alpha = 0.4 - (i / visuals.segments) * 0.25;

      graphics.fillStyle(visuals.color, alpha);
      graphics.fillRect(
        offsetX - finalSize / 2,
        offsetY - finalSize / 2,
        finalSize,
        finalSize
      );

      // Add subtle border
      graphics.lineStyle(1, visuals.color, alpha * 0.5);
      graphics.strokeRect(
        offsetX - finalSize / 2,
        offsetY - finalSize / 2,
        finalSize,
        finalSize
      );
    }

    // Add "eye" indicators at the front (two small bright squares)
    const eyeSize = TILE_SIZE / 2;
    const eyeOffset = baseSize * 0.3;
    const eyeSpread = baseSize * 0.15;

    graphics.fillStyle(0x2a4a6a, 0.6);
    graphics.fillRect(
      Math.cos(angle) * eyeOffset + Math.cos(angle + Math.PI / 2) * eyeSpread - eyeSize / 2,
      Math.sin(angle) * eyeOffset + Math.sin(angle + Math.PI / 2) * eyeSpread - eyeSize / 2,
      eyeSize,
      eyeSize
    );
    graphics.fillRect(
      Math.cos(angle) * eyeOffset - Math.cos(angle + Math.PI / 2) * eyeSpread - eyeSize / 2,
      Math.sin(angle) * eyeOffset - Math.sin(angle + Math.PI / 2) * eyeSpread - eyeSize / 2,
      eyeSize,
      eyeSize
    );
  }

  /**
   * Update all monster animations
   */
  private updateAnimations(): void {
    this.globalTime += 100;

    this.monsters.forEach(sprite => {
      this.drawMonster(sprite);
    });
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    if (this.updateTimer) {
      this.updateTimer.destroy();
      this.updateTimer = null;
    }

    this.monsters.forEach(sprite => {
      sprite.container.destroy();
    });
    this.monsters.clear();
  }
}
